import { useDndContext, useDroppable, horizontalListSortingStrategy, SortableContext, verticalListSortingStrategy } from '../adapters/dndAdapter';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';
import { getListClass } from '../utils/dndClassNames';

/**
 * SortableList - Contenedor para elementos ordenables
 *
 * Combina SortableContext de @dnd-kit con zona droppable
 *
 * Automáticamente aplica la clase 'is-dragging-over' cuando hay un drag activo
 * para mostrar visualmente la zona de drop.
 *
 * @param {string} droppableId - ID único del contenedor
 * @param {Array} items - Array de IDs de items para SortableContext
 * @param {React.ReactNode|Function} children - Contenido o render function
 * @param {string} [className] - Clase CSS adicional
 * @param {string} [direction='vertical'] - Dirección: 'vertical' | 'horizontal'
 * @param {boolean} [disabled=false] - Deshabilitar drop
 * @param {string} [acceptsItemPrefix] - Prefijo de items que acepta (ej: 'material-', 'section-')
 * @param {boolean} [sourceOnly=false] - Si true, lista solo como origen (sin feedback de drop)
 */
const SortableList = ({ droppableId, items, children, className, direction = 'vertical', disabled = false, acceptsItemPrefix, sourceOnly = false }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: droppableId,
    disabled,
  });

  // Detectar si hay un drag activo en el contexto
  const { active } = useDndContext();
  const isDraggingActive = active !== null;

  // Determinar si mostrar feedback visual basado en el tipo de item
  // sourceOnly desactiva todo feedback visual (lista solo como origen de drag)
  const shouldShowDragFeedback =
    !sourceOnly && isDraggingActive && (!acceptsItemPrefix || String(active?.id).startsWith(acceptsItemPrefix));

  // Estrategia de ordenamiento (sourceOnly usa una estrategia que no anima)
  const defaultStrategy = direction === 'horizontal' ? horizontalListSortingStrategy : verticalListSortingStrategy;
  // Para sourceOnly, usamos una estrategia "noop" que no mueve los items
  const noopStrategy = () => ({ x: 0, y: 0, scaleX: 1, scaleY: 1 });
  const strategy = sourceOnly ? noopStrategy : defaultStrategy;

  // Clase que se aplica cuando el item arrastrado es compatible con este contenedor
  const listClassName = clsx(className, getListClass({ isOver: shouldShowDragFeedback }));

  // Renderizar children (función o nodo)
  const renderChildren = () => {
    if (typeof children === 'function') {
      return children({
        isOver,
        isDraggingActive: shouldShowDragFeedback,
      });
    }
    return children;
  };

  return (
    <SortableContext id={droppableId} items={items} strategy={strategy}>
      <div ref={setNodeRef} className={listClassName} data-dnd-zone="">
        {renderChildren()}
      </div>
    </SortableContext>
  );
};

SortableList.propTypes = {
  droppableId: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  className: PropTypes.string,
  direction: PropTypes.oneOf(['vertical', 'horizontal']),
  disabled: PropTypes.bool,
  acceptsItemPrefix: PropTypes.string,
  sourceOnly: PropTypes.bool,
};

export default SortableList;
